import {BREAKPOINTS, getWindowWidth} from '../modules/breakpoint.js';
import {t} from '../localization/translate.js';

/**
 * @typedef {object} MasonryOptions
 * @property {number} [mobileCols=2] - Number of columns on mobile (< md breakpoint)
 * @property {number} [tabletCols=3] - Number of columns on tablet (md to lg-1 breakpoint)
 * @property {number} [desktopCols=4] - Number of columns on desktop (≥ lg breakpoint)
 * @property {string} [itemSelector='.js-masonryGridItem'] - CSS selector for grid items
 * @property {string} [distributionStrategy='round-robin'] - Item distribution strategy
 * @property {string} [columnGapClasses='gap-y-16 md:gap-y-24'] - Tailwind gap utility classes
 */

/**
 * MasonryGrid - High-performance responsive masonry layout
 *
 * Creates responsive columns that adapt to screen size with configurable distribution strategies.
 * Uses ResizeObserver for optimal performance and efficient change detection.
 *
 * Features:
 * - ResizeObserver-based resize detection (more efficient than resize events)
 * - DocumentFragment batching for minimal DOM reflows
 * - Static breakpoint checks for performance
 * - Configurable column counts and distribution strategies
 *
 * Breakpoints:
 * - Uses static values from resources/js/modules/breakpoint.js (single JS source of truth)
 *
 * Distribution Strategies:
 * - 'round-robin': Distributes items sequentially across columns for balanced loading
 * When reaching the last column, cycles back to first column
 * Example with 3 columns: Item1→Col1, Item2→Col2, Item3→Col3, Item4→Col1, etc.
 *
 * Performance optimizations:
 * - ResizeObserver eliminates resize event debouncing needs
 * - DocumentFragment batching minimizes reflows
 * - Static breakpoint comparisons avoid CSS variable reads
 * - Class manipulation batched to reduce style recalculations
 * - Single DOM update per layout change using replaceChildren()
 * - Items moved via fragments instead of individual append() calls
 */
/** @type {string[]} Tailwind classes for FLIP transition animation */
const FLIP_CLASSES = ['transition-transform', 'duration-300', 'ease-out-quint'];
const FLIP_DURATION_MS = 300;

export class MasonryGrid {
    /** @type {number|null} */
    #flipCleanupTimer = null;

    /**
     * @param {HTMLElement} gridElement - The grid container
     * @param {MasonryOptions} [options] - Optional overrides (also read from data-* on the container)
     */
    constructor(gridElement, options = {}) {
        this.grid = gridElement;
        this.options = this.#parseOptions(options);
        /** @type {number|null} */
        this.currentCols = null;
        /** @type {ResizeObserver|null} */
        this.resizeObserver = null;
        /** @type {HTMLElement[]} */
        this.items = [...this.grid.querySelectorAll(this.options.itemSelector)];

        // Expose instance on the DOM element for cross-module access (e.g., saved.js)
        /** @type {MasonryGrid} */
        this.grid.masonryGrid = this;

        if (this.items.length > 0) {
            this.#init();
        }
    }

    /**
     * Parse and validate options from data attributes and constructor arguments.
     * @param {MasonryOptions} options - Configuration options
     * @returns {MasonryOptions} Validated options object
     */
    #parseOptions(options) {
        const data = this.grid.dataset;
        return {
            mobileCols: parseInt(data.mobileCols, 10) || options.mobileCols || 2,
            tabletCols: parseInt(data.tabletCols, 10) || options.tabletCols || 3,
            desktopCols: parseInt(data.desktopCols, 10) || options.desktopCols || 4,
            // Selector for items inside the grid
            itemSelector: data.itemSelector || options.itemSelector || '.js-masonryGridItem',
            // Distribution strategy determines how items are placed into columns
            // 'round-robin': Sequential distribution for balanced column item counts
            distributionStrategy: data.distributionStrategy || options.distributionStrategy || 'round-robin',
            // Vertical spacing between items inside each column
            columnGapClasses: data.columnGapClasses || options.columnGapClasses || 'gap-y-16 md:gap-y-24',
        };
    }

    /**
     * Entry point: initial layout, observers, and progressive reveal.
     */
    #init() {
        this.#updateLayout(true);
        this.#bindResize();
        this.#reveal();
    }

    /**
     * Progressive enhancement: hide layout jitter on first paint and reveal once ready.
     * Removes the transition classes in the same operation as opacity-0 so the
     * reveal is instant (no 200ms fade). The opacity-0 only exists to hide the
     * grid while items are being distributed into columns.
     */
    #reveal() {
        this.grid.setAttribute('data-masonry-initialized', 'true');
        this.grid.classList.remove('opacity-0', 'transition-opacity', 'duration-200');
    }

    /**
     * Set up resize detection using ResizeObserver, throttled to one layout per frame.
     */
    #bindResize() {
        /** @type {number|null} */
        let rafId = null;

        this.resizeObserver = new ResizeObserver(() => {
            if (rafId) return;
            rafId = requestAnimationFrame(() => {
                rafId = null;
                this.#updateLayout();
            });
        });
        this.resizeObserver.observe(this.grid);
    }

    /**
     * Map current viewport width to the configured column count.
     * Uses static breakpoint values from the shared breakpoint module.
     * @returns {number} Column count for current viewport
     */
    #getColumnCount() {
        const width = getWindowWidth();

        if (width < BREAKPOINTS.md) return this.options.mobileCols;
        if (width < BREAKPOINTS.lg) return this.options.tabletCols;
        return this.options.desktopCols;
    }

    /**
     * Trigger a layout update if the column count changed or when forced.
     * @param {boolean} [force]
     */
    #updateLayout(force = false) {
        const newCols = this.#getColumnCount();

        if (!force && newCols === this.currentCols) {
            return;
        }

        this.currentCols = newCols;
        this.#redistributeItems(newCols);
    }

    /**
     * Build columns and move items into them using a batch DOM strategy.
     * @param {number} columnCount
     */
    #redistributeItems(columnCount) {
        // 1) Create column shells + fragments (batch writes later)
        const columns = Array.from({length: columnCount}, (_, index) => ({
            element: document.createElement('div'),
            fragment: document.createDocumentFragment(),
            index,
        }));

        // 2) Apply classes and accessibility attributes to each column.
        //    IMPORTANT: Columns are **flex vertical stacks** with vertical gaps.
        //    We do NOT use grid here; flex columns prevent subgrid issues (like overlapping items).
        columns.forEach((col) => {
            col.element.className = `flex flex-col ${this.options.columnGapClasses}`;
            // Add ARIA attributes for better screen reader support
            col.element.setAttribute('role', 'list');
            col.element.setAttribute('aria-label', t('Column :index of :total', {index: col.index + 1, total: columnCount}));
        });

        // 3) Distribute items into fragments (no DOM mutation yet)
        this.#distributeItems(columns);

        // 4) Batch-append fragments to their columns
        columns.forEach((col) => {
            col.element.append(col.fragment);
        });

        // 5) Replace the grid's children with the finished columns (single write)
        this.#updateGrid(columns.map((c) => c.element), columnCount);
    }

    /**
     * Distributes items across columns based on the selected strategy.
     * @param {Array<{element: HTMLElement, fragment: DocumentFragment, index: number}>} columns
     */
    #distributeItems(columns) {
        if (this.options.distributionStrategy === 'round-robin') {
            // Round-robin distribution: Items are placed sequentially across columns.
            // This ensures near-equal item counts per column regardless of item heights.
            //
            // How it works:
            // - Take each item in source order and place it into the next column's fragment.
            // - Use modulo (%) to wrap back to the first column.
            // - Results in columns having nearly equal number of items (±1).
            //
            // Performance: We append to DocumentFragments first, then mount all at once.
            this.items.forEach((item, index) => {
                const columnIndex = index % columns.length;

                columns[columnIndex].fragment.append(item);
            });
        }

        // Future strategies could go here, e.g., 'shortest-column'.
        // Keep them **pure** (no inline positioning). Example sketch:
        // else if (this.options.distributionStrategy === 'shortest-column') { ... }
    }

    /**
     * Swap the grid contents with the prepared columns and keep classes tidy.
     * We also ensure the container advertises the runtime grid-cols-N class.
     * @param {HTMLElement[]} columns
     * @param {number} columnCount
     */
    #updateGrid(columns, columnCount) {
        // Build a fragment to minimize reflows
        const gridFragment = document.createDocumentFragment();
        columns.forEach((col) => gridFragment.append(col));

        // Remove existing grid-cols-* utilities and apply the new one
        this.grid.classList.remove(
            ...[...this.grid.classList].filter((cls) => cls.startsWith('grid-cols-'))
        );
        this.grid.classList.add(`grid-cols-${columnCount}`);

        // Single DOM update: clear and append
        this.grid.replaceChildren(gridFragment);
    }

    /**
     * Remove an item and shift all subsequent items forward (like removing from
     * a list). Only items after the removed one animate via FLIP — items before
     * it stay perfectly still.
     *
     * @param {HTMLElement} item - The `.js-masonryGridItem` element to remove
     */
    removeItem(item) {
        const removeIndex = this.items.indexOf(item);
        if (removeIndex === -1) {
            item.remove();
            return;
        }

        this.#finishPendingFlip();

        const shiftedItems = this.items.slice(removeIndex + 1);

        /** @type {Map<HTMLElement, DOMRect>} */
        const firstRects = new Map();
        shiftedItems.forEach((element) => firstRects.set(element, element.getBoundingClientRect()));

        this.items.splice(removeIndex, 1);
        item.remove();
        this.#redistributeItems(this.#getColumnCount());

        this.#flipAnimate(firstRects, shiftedItems);
    }

    /**
     * Add new items using the same FLIP technique as {@link removeItem}.
     * Existing items that shift are animated to their new positions.
     * New items simply fade in where they land.
     *
     * @param {HTMLElement[]} newItems - Array of `.js-masonryGridItem` elements
     */
    addItems(newItems) {
        if (newItems.length === 0) {
            return;
        }

        this.#finishPendingFlip();

        /** @type {Map<HTMLElement, DOMRect>} */
        const firstRects = new Map();
        this.items.forEach((item) => firstRects.set(item, item.getBoundingClientRect()));

        newItems.forEach((item) => item.classList.add('opacity-0'));

        this.items.push(...newItems);
        this.#redistributeItems(this.#getColumnCount());

        // New items aren't in firstRects, so #flipAnimate skips them naturally
        this.#flipAnimate(firstRects, this.items);

        // Fade in new items
        requestAnimationFrame(() => {
            newItems.forEach((item) => {
                item.classList.add('transition-opacity', 'duration-500', 'ease-out-quint');
                item.classList.remove('opacity-0');
            });
        });

        // Override the FLIP cleanup timer to also remove fade-in classes
        clearTimeout(this.#flipCleanupTimer);
        this.#flipCleanupTimer = setTimeout(() => {
            this.#finishPendingFlip();
            newItems.forEach((item) => {
                item.classList.remove('transition-opacity', 'duration-500', 'ease-out-quint');
            });
        }, 500);
    }

    /**
     * Run the FLIP animation for items whose previous positions are in firstRects.
     * Computes position deltas, applies inverse transforms, then animates to final positions.
     *
     * @param {Map<HTMLElement, DOMRect>} firstRects - Pre-layout positions
     * @param {HTMLElement[]} items - Items to animate (must already be in the DOM at new positions)
     */
    #flipAnimate(firstRects, items) {
        items.forEach((element) => {
            const first = firstRects.get(element);
            if (!first) {
                return;
            }
            const last = element.getBoundingClientRect();
            const dx = first.left - last.left;
            const dy = first.top - last.top;
            if (dx === 0 && dy === 0) {
                return;
            }
            element.style.transform = `translate(${dx}px, ${dy}px)`;
        });

        requestAnimationFrame(() => {
            items.forEach((element) => {
                if (!element.style.transform) {
                    return;
                }
                element.classList.add(...FLIP_CLASSES);
                element.style.transform = '';
            });
        });

        this.#flipCleanupTimer = setTimeout(() => this.#finishPendingFlip(), FLIP_DURATION_MS);
    }

    /**
     * Instantly complete any in-progress FLIP animation.
     */
    #finishPendingFlip() {
        if (this.#flipCleanupTimer) {
            clearTimeout(this.#flipCleanupTimer);
            this.#flipCleanupTimer = null;
        }
        this.items.forEach((item) => {
            item.classList.remove(...FLIP_CLASSES);
            item.style.transform = '';
        });
    }

    /**
     * Clean up observers. (Call when unmounting/tearing down.)
     */
    destroy() {
        this.#finishPendingFlip();
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }
}

/**
 * Initialize masonry for a single element with high-performance resize detection.
 *
 * Creates a responsive masonry grid that automatically adapts to size changes
 * using ResizeObserver for optimal performance.
 *
 * @param {HTMLElement} element - The container element to transform into a masonry grid
 * @param {object} [options] - Configuration options (can also be set via data-* attributes)
 * @param {number} [options.mobileCols] - Number of columns on mobile (< md breakpoint)
 * @param {number} [options.tabletCols] - Number of columns on tablet (md to lg-1 breakpoint)
 * @param {number} [options.desktopCols] - Number of columns on desktop (≥ lg breakpoint)
 * @param {string} [options.itemSelector] - CSS selector for grid items
 * @param {string} [options.distributionStrategy] - Item distribution strategy
 * @param {string} [options.columnGapClasses] - Tailwind gap utility classes
 * @returns {MasonryGrid|undefined} The masonry instance or undefined if element already initialized
 *
 * @example
 * // Basic usage with HTML data attributes
 * // <div class="js-masonryGrid" data-mobile-cols="1" data-tablet-cols="2" data-desktop-cols="3">
 * //   <div class="js-masonryGridItem">Item 1</div>
 * //   <div class="js-masonryGridItem">Item 2</div>
 * // </div>
 * const masonry = responsiveMasonry(document.querySelector('.js-masonryGrid'));
 *
 * @example
 * // Custom configuration via options object
 * const masonry = responsiveMasonry(element, {
 *   mobileCols: 1,
 *   tabletCols: 2,
 *   desktopCols: 4,
 *   distributionStrategy: 'round-robin',
 *   columnGapClasses: 'gap-y-8 md:gap-y-12'
 * });
 *
 * @example
 * // Clean up when component unmounts
 * const masonry = responsiveMasonry(element);
 * // Later...
 * masonry?.destroy(); // Removes observers
 *
 * @example
 * // Advanced configuration with data attributes
 * // <div class="js-masonryGrid"
 * //      data-mobile-cols="1"
 * //      data-tablet-cols="2"
 * //      data-desktop-cols="3"
 * //      data-distribution-strategy="round-robin"
 * //      data-column-gap-classes="gap-y-8 lg:gap-y-12">
 * //   <article class="js-masonryGridItem">...</article>
 * //   <article class="js-masonryGridItem">...</article>
 * // </div>
 * const masonry = responsiveMasonry(document.querySelector('.js-masonryGrid'));
 */
export function responsiveMasonry(element, options = {}) {
    if (!element || element.hasAttribute('data-masonry-initialized')) {
        return;
    }

    return new MasonryGrid(element, options);
}

/**
 * Auto-initialize all masonry grids on page
 */
function autoInit() {
    document.querySelectorAll('.js-masonryGrid').forEach(container => {
        responsiveMasonry(container);
    });
}

document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', autoInit, {once: true})
    : autoInit();

// Re-initialize new masonry grids after HTMX swaps (e.g., sort dropdown replaces the container)
document.addEventListener('htmx:afterSwap', () => autoInit());
